import { z } from 'zod';

/** Campos do cadastro que a planilha e a VOLL comparam: nome (com sobrenome), nascimento, CPF e sexo. */
export const MICE_PROFILE_FIELDS = ['name', 'birthdate', 'cpf', 'gender'] as const;
export type MiceProfileField = (typeof MICE_PROFILE_FIELDS)[number];
export const MiceProfileFieldSchema = z.enum(MICE_PROFILE_FIELDS);

/**
 * `voll`: só a VOLL tem o valor; `equal`: planilha e VOLL batem; `to_complete`: só a planilha tem
 * (ou nenhuma tem) e falta gravar; `divergent`: os dois têm e diferem.
 */
export const MiceProfileFieldStateSchema = z.enum(['voll', 'equal', 'to_complete', 'divergent']);
export type MiceProfileFieldState = z.infer<typeof MiceProfileFieldStateSchema>;

export const MiceProfileFieldViewSchema = z.object({
  state: MiceProfileFieldStateSchema,
  /** Nome e nascimento em claro; CPF mascarado (`***.456.789-**`). */
  sheet: z.string().nullable(),
  /** Idem. */
  voll: z.string().nullable(),
});
export type MiceProfileFieldView = z.infer<typeof MiceProfileFieldViewSchema>;

/** Sexo como a reserva o leva. Sem ele o legado emite como masculino (#5527): a compra o exige. */
export const MiceGenderSchema = z.enum(['M', 'F']);
export type MiceGender = z.infer<typeof MiceGenderSchema>;

export const MiceProfileCheckViewSchema = z.object({
  status: z.enum(['ok', 'pending', 'incomplete']),
  fields: z.object({
    name: MiceProfileFieldViewSchema,
    birthdate: MiceProfileFieldViewSchema,
    cpf: MiceProfileFieldViewSchema,
    /** `M`/`F`. Checagem guardada antes do sexo: só da VOLL, sem valor (a releitura `gender` a refaz). */
    gender: MiceProfileFieldViewSchema.default({ state: 'voll', sheet: null, voll: null }),
  }),
  /** O que falta no que vale para a compra. */
  missing: z.array(z.enum(MICE_PROFILE_FIELDS)),
});
export type MiceProfileCheckView = z.infer<typeof MiceProfileCheckViewSchema>;

/** `null` = a validação ainda não rodou (ou o documento é anterior a ela): não pede ação. */
export const profileNeedsAction = (c: MiceProfileCheckView | null): boolean =>
  c !== null && c.status !== 'ok';

const cpfCheckDigit = (digits: number[]): number => {
  const sum = digits.reduce((total, digit, i) => total + digit * (digits.length + 1 - i), 0);
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

/** 11 dígitos com os dois verificadores certos; sequência repetida (`111...`) não é CPF. */
export const isValidCpf = (digits: string): boolean => {
  if (!/^\d{11}$/.test(digits) || /^(\d)\1{10}$/.test(digits)) return false;
  const numbers = Array.from(digits, Number);
  const first = cpfCheckDigit(numbers.slice(0, 9));
  const second = cpfCheckDigit([...numbers.slice(0, 9), first]);
  return numbers[9] === first && numbers[10] === second;
};

/** `12345678909` → `123.456.789-09`, à medida que se digita; ignora o que não é dígito. */
export function maskCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  const head = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9)].filter(Boolean).join('.');
  return d.length > 9 ? `${head}-${d.slice(9)}` : head;
}

/** `sheet`: grava o valor da planilha na VOLL; `voll`: mantém o da VOLL e descarta o da planilha. */
export const MiceProfileChoiceSchema = z.enum(['sheet', 'voll']);
export type MiceProfileChoice = z.infer<typeof MiceProfileChoiceSchema>;

const NamePartSchema = z.string().trim().min(1).max(80);
/** Nascimento mais antigo aceito (planilha e formulário); o teto é hoje. */
export const MICE_BIRTHDATE_MIN = '1900-01-01';

/**
 * Decisão do operador sobre o cadastro de UM viajante (spec B4). `typed` é o formulário: Nome e
 * Sobrenome sempre juntos (o MICE nunca divide um nome), nascimento `AAAA-MM-DD`, CPF com 11
 * dígitos e verificadores certos e sexo `M`/`F`. Nada é preenchido sozinho.
 */
export const MiceProfileResolveSchema = z
  .object({
    choices: z
      .object({
        name: MiceProfileChoiceSchema.optional(),
        birthdate: MiceProfileChoiceSchema.optional(),
        cpf: MiceProfileChoiceSchema.optional(),
        gender: MiceProfileChoiceSchema.optional(),
      })
      .strict(),
    typed: z
      .object({
        firstName: NamePartSchema.optional(),
        lastName: NamePartSchema.optional(),
        birthdate: z.iso
          .date()
          .refine(
            (date) => date >= MICE_BIRTHDATE_MIN && date <= new Date().toISOString().slice(0, 10),
            'Data de nascimento inválida',
          )
          .optional(),
        cpf: z.string().refine(isValidCpf, 'CPF inválido').optional(),
        gender: MiceGenderSchema.optional(),
      })
      .strict()
      .refine((t) => (t.firstName === undefined) === (t.lastName === undefined), {
        message: 'Nome e sobrenome vão juntos',
      })
      .optional(),
  })
  .strict()
  .refine(
    (body) => Object.keys(body.choices).length > 0 || Object.keys(body.typed ?? {}).length > 0,
    {
      message: 'Nenhuma decisão',
    },
  );
export type MiceProfileResolve = z.infer<typeof MiceProfileResolveSchema>;

/**
 * Ações em lote da seleção: `sheet_to_complete` grava só os campos `to_complete` (divergência se
 * decide viajante por viajante); `keep_voll` descarta o que veio da planilha.
 */
export const MiceProfileBulkResolveSchema = z
  .object({
    travelerIds: z.array(z.string().min(1)).min(1).max(200),
    choice: z.enum(['sheet_to_complete', 'keep_voll']),
  })
  .strict();
export type MiceProfileBulkResolve = z.infer<typeof MiceProfileBulkResolveSchema>;

export const MiceProfileBulkResultSchema = z.object({
  updated: z.number().int().nonnegative(),
  failed: z.array(z.object({ travelerId: z.string(), message: z.string() })),
});
export type MiceProfileBulkResult = z.infer<typeof MiceProfileBulkResultSchema>;
